import { useCallback, useEffect, useRef, useState } from 'react';
import { currentVersion, options } from './config';
import { ViewerContainer } from './components/ViewerContainer';
import { Resize } from './components/Resize';
import { useThemeManagement } from './theme/themeManagement';
import { useViewManagement } from './view/viewManagement';
import { useModelManagement } from './models/modelManagement';
import { useSelectManagement } from './selection/selectionManagement';
import { useIsolateElements } from './view/useIsolateElements';
import { useHideElements } from './view/useHideElements';
import { errorMessageMap } from './errors.ts/errorMessageMap';
import { jsx as _jsx } from "react/jsx-runtime";
export const ForgeViewer = ({
  fetchToken,
  models,
  onError,
  focusOnElements,
  selectElements,
  themeData,
  onModelLoaded,
  isolateElements,
  hideAllExceptElements,
  onSelectElements,
  onLoadModelError,
  extensionsToLoad,
  defaultColor,
  onModelUnloaded,
  loadModelByUrl,
  getModelUrl
}) => {
  const viewerRef = useRef(null);
  const [aggregatedView, setAggregatedView] = useState(null);
  const {
    geometryLoaded,
    invertedExternalIdMap
  } = useModelManagement({
    models,
    aggregatedView,
    onModelLoaded,
    onLoadModelError,
    onModelUnloaded,
    loadModelByUrl,
    getModelUrl
  });
  useSelectManagement({
    viewer: (aggregatedView === null || aggregatedView === void 0 ? void 0 : aggregatedView.viewer) || null,
    geometryLoaded,
    selectElements,
    invertedExternalIdMap,
    onSelectElements
  });
  useViewManagement({
    viewer: (aggregatedView === null || aggregatedView === void 0 ? void 0 : aggregatedView.viewer) || null,
    geometryLoaded,
    focusOnElements
  });
  useThemeManagement({
    viewer: (aggregatedView === null || aggregatedView === void 0 ? void 0 : aggregatedView.viewer) || null,
    geometryLoaded,
    themeData,
    defaultColor,
    loadModelByUrl
  });
  useIsolateElements({
    geometryLoaded,
    viewer: (aggregatedView === null || aggregatedView === void 0 ? void 0 : aggregatedView.viewer) || null,
    isolateElements
  });
  useHideElements({
    geometryLoaded,
    viewer: (aggregatedView === null || aggregatedView === void 0 ? void 0 : aggregatedView.viewer) || null,
    hideAllExceptElements
  });
  useEffect(() => {
    if (aggregatedView) {
      aggregatedView.viewer.loadExtension('Autodesk.DocumentBrowser');
      return () => {
        aggregatedView.viewer.unloadExtension('Autodesk.DocumentBrowser');
      };
    }
  }, [aggregatedView]);
  const loadExtension = useCallback((viewer, extensions) => {
    extensions.forEach(({
      extension,
      extensionName,
      callback
    }) => {
      Autodesk.Viewing.theExtensionManager.registerExtension(extensionName, extension);
      viewer.loadExtension(extensionName, callback);
    });
  }, []);
  const onLoad = useCallback(() => {
    const viewerElement = viewerRef.current;

    if (viewerElement) {
      Autodesk.Viewing.Initializer({
        env: 'AutodeskProduction',

        async getAccessToken(onAccessToken) {
          const response = await fetchToken();
          const expiresAt = Math.round((response.expiresAt - new Date().getTime()) / 1000);
          onAccessToken(response.accessToken, expiresAt);
        },

        api: 'derivativeV2'
      }, async () => {
        const aggregatedView = await new Autodesk.Viewing.AggregatedView();
        aggregatedView.init(viewerElement, options);
        setAggregatedView(aggregatedView);

        if (extensionsToLoad) {
          const extensions = await extensionsToLoad();
          loadExtension(aggregatedView.viewer, extensions);
        }
      });
    }
  }, [fetchToken, extensionsToLoad, loadExtension]);
  const onResize = useCallback(() => {
    if (aggregatedView) {
      aggregatedView.viewer.resize();
    }
  }, [aggregatedView]);
  const onLoadScriptError = useCallback(() => {
    onError({
      errorMessage: errorMessageMap.LOAD_AUTODESK_SCRIPT,
      urn: ''
    });
  }, [onError]);
  return /*#__PURE__*/_jsx(Resize, {
    onResize: onResize,
    resizeNode: /*#__PURE__*/_jsx(ViewerContainer, {
      onError: onLoadScriptError,
      onLoad: onLoad,
      version: currentVersion,
      ref: viewerRef
    })
  });
};